import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Grow, Paper, Popper, Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { LocalDataTrack, TwilioError } from 'twilio-video';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import useIsUserActive from './useIsUserActive/useIsUserActive';
import useRoomState from '../../hooks/useRoomState/useRoomState';
import ToggleVideoEMDR from './ToggleVideoEMDR/ToggleVideoEMDR';
import CompositeAudioButton from '../Controls/CompositeAudioButton/CompositeAudioButton';
import CompositeVideoButton from '../Controls/CompositeVideoButton/CompositeVideoButton';
import CompositeBackgroundButton from '../Controls/CompositeBackgroundButton/CompositeBackgroundButton';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import ChatButton from '../ChatButton/ChatButton';
import useRemoteDatatrack from '../../hooks/useDatatrackParticipant/useRemoteDatatrack';
import MediaControls from './MediaControls/MediaControls';
import useAppContext from '../../hooks/useAppContext/useAppContext';
import clsx from 'clsx';
import usePractitionerControlsContext from '../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';
import useMediaContext from '../../hooks/useMediaContextHook/useMediaContext';
import ErrorDialog from '../ErrorDialog/ErrorDialog';
import { ModalUI } from '../UI/Modal/Modal';
import { ControlsPractitionerStyle } from './style';
import MediaModal from './MediaModal/MediaModal';
import ReactPlayer from 'react-player/lazy';
import { MediaTypes } from '../../commands/practitioner/enums/MediaTypes';
import useUploadContext from '../../hooks/useUploadContext/useUploadContext';

const useStyles = makeStyles<Theme>(theme => ({
  overall: {
    display: 'inline-flex',
    position: 'absolute',
    bottom: '0%',
    width: '75%',
    height: '140px',
    justifyItems: 'bottom',
    alignItems: 'center',
    zIndex: 3,
  },
  containerAudio: {
    display: 'flex',
    position: 'absolute',
    left: '0%',
    zIndex: 1,
    transition: 'opacity 1.2s, transform 1.2s, visibility 0s 1.2s',
    visibility: 'visible',
    maxWidth: 'min-content',
    justifyItems: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      bottom: `${theme.sidebarMobileHeight + 3}px`,
    },
  },
  containerVideo: {
    display: 'flex',
    position: 'absolute',
    zIndex: 1,
    marginLeft: '75px',
    transition: 'opacity 1.2s, transform 1.2s, visibility 0s 1.2s',
    visibility: 'visible',
    maxWidth: 'min-content',
    justifyItems: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      bottom: `${theme.sidebarMobileHeight + 3}px`,
    },
  },
  containerBackground: {
    display: 'flex',
    position: 'absolute',
    zIndex: 1,
    marginLeft: '145px',
    transition: 'opacity 1.2s, transform 1.2s, visibility 0s 1.2s',
    visibility: 'visible',
    maxWidth: 'min-content',
    justifyItems: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      bottom: `${theme.sidebarMobileHeight + 3}px`,
    },
  },
  containerMiddle: {
    display: 'flex',
    position: 'absolute',
    left: '35%',
    zIndex: 1,
    transition: 'opacity 1.2s, transform 1.2s, visibility 0s 1.2s',
    visibility: 'visible',
    padding: '0 2em',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      bottom: `${theme.sidebarMobileHeight + 3}px`,
    },
  },
  containerRight: {
    display: 'flex',
    position: 'absolute',
    right: '5%',
    zIndex: 1,
    transition: 'opacity 1.2s, transform 1.2s, visibility 0s 1.2s',
    visibility: 'visible',
    maxWidth: 'min-content',
    [theme.breakpoints.down('xs')]: {
      bottom: `${theme.sidebarMobileHeight + 3}px`,
      padding: 0,
    },
    width: `${window.innerWidth}`,
  },
}));

interface IControlPractitionerProps {
  currentState: any;
  toggleClientCallback?: () => void;
}

export default function ControlsPractitioner(props: IControlPractitionerProps) {
  const { hasDevice } = useAppContext();
  const {
    taskHasStarted,
    setOpenMediaModal,
    openMediaModal,
    practitionerMediaType,
    practitionerMediaURI,
    setIsMediaLoaded,
    status,
  } = usePractitionerControlsContext();
  const { isUploadTabOpen } = useUploadContext();
  const { setDiveIndex, setFirstDiveInd } = useMediaContext();
  const classes = useStyles();
  const { t } = useTranslation();
  const { localTracks } = useVideoContext();
  const isUserActive = useIsUserActive();
  const roomState = useRoomState();
  const remoteDatatrack = useRemoteDatatrack();
  const [isVideoActive, setVideoActive] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [generalError, setGeneralError] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const localDataTrack = localTracks.find(track => track.kind === 'data') as LocalDataTrack;
  const isDisconnected = roomState === 'disconnected';
  const showControls = isUserActive || roomState === 'disconnected';
  const backgroundStyling = { backgroundColor: '#e1e0e1' };
  const { mediaState, errorMessage } = props.currentState;

  const { mediaModal, wrapper } = ControlsPractitionerStyle;

  useEffect(() => {
    if (isDisconnected) {
      setVideoActive(true);
    }
  }, [taskHasStarted, isDisconnected]);

  useEffect(() => {
    let canBePlayed = false;
    if (isUploadTabOpen) {
      canBePlayed = status;
    } else {
      switch (practitionerMediaType) {
        case MediaTypes.VIDEO:
          canBePlayed = ReactPlayer.canPlay(practitionerMediaURI);
          break;
        case MediaTypes.AUDIO:
          canBePlayed = ReactPlayer.canPlay(practitionerMediaURI);
          break;
        case MediaTypes.IMAGE:
          const img = new Image();
          img.src = practitionerMediaURI;
          img.onload = () => setIsMediaLoaded(true);
          img.onerror = () => setIsMediaLoaded(false);
          break;
        default:
          break;
      }
    }

    // If Video or Audio, check against ReactPlayer, else check if image type URL

    setIsMediaLoaded(canBePlayed);
  }, [practitionerMediaURI, practitionerMediaType]);

  useEffect(() => {
    // Register for report on Video Toggle changes driven by client side actions
    if (remoteDatatrack) {
      remoteDatatrack.on('subscribed', track => {
        track.on('message', (msg: any) => {
          if (msg) {
            const { type, data } = JSON.parse(msg);
            if ((type as string) === 'VideoFrame Report') {
              setVideoActive(data);
            }
          }
        });
      });
    }
    return () => {
      if (remoteDatatrack) {
        remoteDatatrack.off('subscribed', track => {
          track.off('message', () => {});
        });
      }
    };
  }, [remoteDatatrack, setVideoActive]);

  const closeModal = useCallback(() => {
    setOpenMediaModal(false);
    setDiveIndex([]);
    setFirstDiveInd(true);
  }, []);

  useEffect(() => {
    setError(errorMessage);
  }, [errorMessage]);

  return (
    <Box sx={wrapper} className={clsx(classes.overall)}>
      {error ? <ErrorDialog dismissError={() => setError('')} error={new Error(error) as TwilioError} /> : null}
      {generalError ? (
        <ErrorDialog dismissError={() => setGeneralError('')} error={new Error(generalError) as TwilioError} />
      ) : null}
      <ModalUI modalSx={mediaModal} className={'media-modal'} open={openMediaModal} onCloseHandler={closeModal}>
        <MediaModal />
      </ModalUI>
      {!isDisconnected ? (
        <>
          <div className={clsx(classes.containerAudio, { showControls })}>
            <CompositeAudioButton />
          </div>
          <div className={clsx(classes.containerVideo, { showControls })}>
            <CompositeVideoButton />
          </div>
          <div className={clsx(classes.containerBackground, { showControls })}>
            <CompositeBackgroundButton />
          </div>
        </>
      ) : null}
      <div className={clsx(classes.containerMiddle, { showControls })}>
        {!hasDevice && !isDisconnected ? (
          <ToggleVideoEMDR
            t={t}
            isVideoEnabled={isVideoActive}
            setVideoEnabled={setVideoActive}
            remoteDatatrack={remoteDatatrack}
            localDatatrack={localDataTrack}
            isDisabled={taskHasStarted}
          />
        ) : null}
        <MediaControls modalIsOpened={openMediaModal} setModalIsOpened={setOpenMediaModal} mediaState={mediaState} />
      </div>
      {!isDisconnected ? (
        <div className={clsx(classes.containerRight, { showControls })}>
          <ChatButton
            t={t}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            localDatatrack={localDataTrack}
            remoteDatatrack={remoteDatatrack}
            isDisconnected={isDisconnected}
          />
        </div>
      ) : null}
    </Box>
  );
}
